'use client'

import * as React from 'react'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, GlobeIcon } from 'lucide-react'
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker'

import { Button, buttonVariants } from '@/components/ui/button'
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
} from '@/components/ui/combobox'
import { cn } from '@/utilities/ui'
import {
  CALENDAR_TIMEZONE_OPTIONS,
  composeTimeFrom12Hour,
  formatGmtOffset,
  generateTimeSlots,
  parseTimeTo12Hour,
  timezoneCityLabel,
} from '@/utilities/zonedDateTime'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'

import type { TimeParts12 } from '@/utilities/zonedDateTime'

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'primary',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'group/calendar bg-background p-3 [--cell-size:2rem] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-full rounded-md border-border'),
        months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-4',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-1 select-none aria-disabled:opacity-50',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size) pt-0.5',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'relative rounded-md border border-input shadow-xs has-focus:border-ring has-focus:ring-[3px] has-focus:ring-ring/50',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn('absolute inset-0 bg-popover opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'font-medium select-none',
          captionLayout === 'label'
            ? 'text-sm'
            : 'flex h-8 items-center gap-1 rounded-md pr-1 pl-2 text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground',
          defaultClassNames.caption_label,
        ),
        table: 'w-full border-collapse',
        weekdays: cn('flex gap-1', defaultClassNames.weekdays),
        weekday: cn(
          'flex-1 rounded-md text-[0.8rem] font-normal text-muted-foreground select-none',
          defaultClassNames.weekday,
        ),
        week: cn('mt-2 flex w-full gap-1', defaultClassNames.week),
        week_number_header: cn('w-(--cell-size) select-none', defaultClassNames.week_number_header),
        week_number: cn(
          'text-[0.8rem] text-muted-foreground select-none',
          defaultClassNames.week_number,
        ),
        day: cn(
          'group/day relative aspect-square size-full p-0 text-center select-none [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
          defaultClassNames.day,
        ),
        range_start: cn('rounded-l-md bg-accent', defaultClassNames.range_start),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('rounded-r-md bg-accent', defaultClassNames.range_end),
        today: cn(
          'rounded-md bg-accent-foreground text-accent data-[selected=true]:bg-none',
          defaultClassNames.today,
        ),
        outside: cn(
          'text-muted-foreground aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={cn('size-4', className)} {...props} />
          }

          if (orientation === 'right') {
            return <ChevronRightIcon className={cn('size-4', className)} {...props} />
          }

          return <ChevronDownIcon className={cn('size-4', className)} {...props} />
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'inline-flex items-center justify-center rounded-md text-sm transition-colors focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
        'p-1 hover:bg-accent hover:text-accent-foreground',
        'data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[selected-single=true]:hover:bg-primary data-[selected-single=true]:hover:text-primary-foreground',
        'data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground',
        'data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground',
        'data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground',
        'group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50',
        'flex aspect-square h-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal',
        'data-[range-end=true]:rounded-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md',
        'group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px]',
        '[&>span]:text-xs [&>span]:opacity-70',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  )
}

const TIME_SLOTS = generateTimeSlots(30)

type CalendarTimePickerNativeProps = {
  variant?: 'native'
} & React.ComponentProps<'input'>

type TimeSlot = (typeof TIME_SLOTS)[number]

type CalendarTimePickerListProps = {
  variant: 'list'
  value?: string
  onValueChange: (time: string) => void
  placeholder?: string
  className?: string
  triggerClassName?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  containerRef?: React.RefObject<HTMLElement | null>
}

type CalendarTimePickerProps = CalendarTimePickerNativeProps | CalendarTimePickerListProps

function CalendarTimePicker(props: CalendarTimePickerProps) {
  if (props.variant === 'list') {
    return <CalendarTimePickerList {...props} />
  }

  const { variant: _variant, className, ...inputProps } = props
  return <input type="time" {...inputProps} className={cn(className)} />
}

const EMPTY_TIME_PARTS: TimeParts12 = { hour: '', minute: '', period: 'AM' }

function normalizeTimeParts(parts: TimeParts12): TimeParts12 | null {
  if (!parts.hour.trim()) return null
  let hour = parseInt(parts.hour, 10)
  if (Number.isNaN(hour)) return null
  hour = Math.min(12, Math.max(1, hour))

  let minute = parseInt(parts.minute.trim() === '' ? '0' : parts.minute, 10)
  if (Number.isNaN(minute)) minute = 0
  minute = Math.min(59, Math.max(0, minute))

  return {
    hour: String(hour),
    minute: String(minute).padStart(2, '0'),
    period: parts.period,
  }
}

function CalendarTimePickerList({
  value = '',
  onValueChange,
  placeholder = 'Time',
  className,
  triggerClassName,
  open: openProp,
  onOpenChange,
  containerRef,
}: CalendarTimePickerListProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const open = openProp ?? uncontrolledOpen
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next)
    onOpenChange?.(next)
  }
  const hourRef = React.useRef<HTMLInputElement>(null)
  const minuteRef = React.useRef<HTMLInputElement>(null)
  const periodRef = React.useRef<HTMLButtonElement>(null)
  const editingRef = React.useRef(false)
  const draftRef = React.useRef<TimeParts12>(parseTimeTo12Hour(value) ?? EMPTY_TIME_PARTS)

  const [draft, setDraftState] = React.useState<TimeParts12>(
    () => parseTimeTo12Hour(value) ?? EMPTY_TIME_PARTS,
  )

  const selectedSlot = React.useMemo(
    () => TIME_SLOTS.find((slot) => slot.value === value) ?? null,
    [value],
  )

  const setDraft = (next: TimeParts12 | ((prev: TimeParts12) => TimeParts12)) => {
    setDraftState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next
      draftRef.current = resolved
      return resolved
    })
  }

  // Sync from parent only when the user isn't typing (avoids clobbering "12" after "1").
  React.useEffect(() => {
    if (editingRef.current) return
    const next = parseTimeTo12Hour(value) ?? EMPTY_TIME_PARTS
    draftRef.current = next
    setDraftState(next)
  }, [value])

  const emitParts = (parts: TimeParts12) => {
    const normalized = normalizeTimeParts(parts)
    if (!normalized) return
    setDraft(normalized)
    const composed = composeTimeFrom12Hour(normalized)
    if (composed) onValueChange(composed)
  }

  const handleHourChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 2)
    setDraft((prev) => ({ ...prev, hour: digits }))
    if (digits.length === 2 || (digits.length === 1 && parseInt(digits, 10) > 1)) {
      requestAnimationFrame(() => {
        minuteRef.current?.focus()
        minuteRef.current?.select()
      })
    }
  }

  const handleHourBlur = () => {
    editingRef.current = false
    const current = draftRef.current
    if (!current.hour.trim()) return
    // Keep partial minute draft while normalizing hour; emit only when minute present.
    let hour = parseInt(current.hour, 10)
    if (Number.isNaN(hour)) {
      setDraft((prev) => ({ ...prev, hour: '' }))
      return
    }
    hour = Math.min(12, Math.max(1, hour))
    const next = { ...current, hour: String(hour) }
    setDraft(next)
    if (next.minute.trim()) emitParts(next)
  }

  const handleMinuteChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 2)
    setDraft((prev) => ({ ...prev, minute: digits }))
    if (digits.length === 2) {
      requestAnimationFrame(() => {
        periodRef.current?.focus()
      })
    }
  }

  const handleMinuteBlur = () => {
    editingRef.current = false
    const current = draftRef.current
    if (!current.hour.trim() && !current.minute.trim()) return
    emitParts({
      hour: current.hour.trim() ? current.hour : '12',
      minute: current.minute,
      period: current.period,
    })
  }

  const togglePeriod = () => {
    const current = draftRef.current
    const nextPeriod = current.period === 'AM' ? 'PM' : 'AM'
    emitParts({
      hour: current.hour.trim() ? current.hour : '12',
      minute: current.minute.trim() ? current.minute : '00',
      period: nextPeriod,
    })
  }

  const isEmpty = !value && !draft.hour && !draft.minute

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-lg bg-muted-foreground/20 py-[5px] pr-1 pl-2 text-sm font-semibold text-primary focus-within:bg-muted-foreground/30',
        open && 'bg-muted-foreground/30',
        isEmpty && 'font-normal text-muted-foreground',
        triggerClassName,
        className,
      )}
    >
      <label className="sr-only">{placeholder} hour</label>
      <input
        ref={hourRef}
        type="text"
        inputMode="numeric"
        maxLength={2}
        placeholder="12"
        aria-label={`${placeholder} hour`}
        value={draft.hour}
        onChange={(e) => handleHourChange(e.target.value)}
        onFocus={(e) => {
          editingRef.current = true
          e.target.select()
        }}
        onBlur={handleHourBlur}
        onKeyDown={(e) => {
          if (e.key === ':' || e.key === 'ArrowRight') {
            e.preventDefault()
            minuteRef.current?.focus()
            minuteRef.current?.select()
          }
        }}
        className="w-4 bg-transparent p-0 text-center text-sm font-semibold tabular-nums outline-none placeholder:text-muted-foreground"
      />
      <span className="text-muted-foreground select-none" aria-hidden>
        :
      </span>
      <label className="sr-only">{placeholder} minutes</label>
      <input
        ref={minuteRef}
        type="text"
        inputMode="numeric"
        maxLength={2}
        placeholder="00"
        aria-label={`${placeholder} minutes`}
        value={draft.minute}
        onChange={(e) => handleMinuteChange(e.target.value)}
        onFocus={(e) => {
          editingRef.current = true
          e.target.select()
        }}
        onBlur={handleMinuteBlur}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault()
            hourRef.current?.focus()
            hourRef.current?.select()
          }
          if (e.key === 'ArrowRight') {
            e.preventDefault()
            periodRef.current?.focus()
          }
        }}
        className="w-5 bg-transparent p-0 text-center text-sm font-semibold tabular-nums outline-none placeholder:text-muted-foreground"
      />
      <button
        ref={periodRef}
        type="button"
        aria-label={`${placeholder} AM/PM, currently ${draft.period}`}
        onClick={togglePeriod}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === ' ') {
            e.preventDefault()
            togglePeriod()
          }
        }}
        className={cn(
          'ml-0.5 rounded text-xs leading-5 font-semibold text-foreground ring-evergreen-500 ring-offset-transparent transition-all outline-none hover:bg-muted focus-visible:ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          isEmpty && 'text-muted-foreground',
        )}
      >
        {draft.period}
      </button>

      <Combobox
        items={TIME_SLOTS}
        value={selectedSlot}
        onValueChange={(item) => {
          if (!item) return
          editingRef.current = false
          onValueChange((item as TimeSlot).value)
          setOpen(false)
        }}
        itemToStringLabel={(item) => (item as TimeSlot).label}
        itemToStringValue={(item) => (item as TimeSlot).value}
        isItemEqualToValue={(a, b) => !!a && !!b && (a as TimeSlot).value === (b as TimeSlot).value}
        open={open}
        onOpenChange={(next) => setOpen(next)}
      >
        <ComboboxTrigger
          showChevron={false}
          render={
            <button
              type="button"
              aria-label={`Choose ${placeholder} from list`}
              className="ml-0.5 rounded p-1 text-muted-foreground ring-evergreen-500 ring-offset-transparent transition-all outline-none hover:bg-muted hover:text-foreground focus-visible:ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              // className="ml-0.5 rounded border-2 border-evergreen-600 bg-background p-1 text-muted-foreground ring-evergreen-500 ring-offset-transparent transition-all outline-none selection:bg-primary selection:text-primary-foreground hover:bg-muted hover:text-foreground focus:ring focus:ring-evergreen-500 focus:ring-offset-transparent focus:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40"

              // border-2 border-evergreen-600 bg-background selection:bg-primary selection:text-primary-foreground focus:ring focus:ring-evergreen-500 focus:ring-offset-transparent focus:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40

              // className="h-10 w-full min-w-0 appearance-none rounded-xl border-2 border-evergreen-600 bg-background py-1.5 pr-8 pl-4 text-base shadow-none transition-[color,box-shadow] outline-none select-none  placeholder:text-muted-foreground  data-[size=sm]:h-8 "
            />
          }
        >
          <ChevronDownIcon className="size-3.5" />
        </ComboboxTrigger>
        <ComboboxContent className="w-36" align="end" container={containerRef}>
          <ComboboxList>
            {(item: TimeSlot) => (
              <ComboboxItem key={item.value} value={item}>
                {item.label}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  )
}

type CalendarTimeZoneSelectProps = {
  variant?: 'select'
  value: string
  onValueChange: (timeZone: string) => void
} & Omit<React.ComponentProps<typeof Select>, 'value' | 'onValueChange' | 'defaultValue'>

type CalendarTimeZoneSearchableProps = {
  variant: 'searchable'
  value: string
  onValueChange: (timeZone: string) => void
  /** Custom trigger; defaults to globe + GMT + city */
  trigger?: React.ReactNode
  triggerClassName?: string
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  containerRef?: React.RefObject<HTMLElement | null>
}

type CalendarTimeZoneProps = CalendarTimeZoneSelectProps | CalendarTimeZoneSearchableProps

function CalendarTimeZone(props: CalendarTimeZoneProps) {
  if (props.variant === 'searchable') {
    return <CalendarTimeZoneSearchable {...props} />
  }

  const { variant: _variant, value, onValueChange, ...selectProps } = props
  return (
    <Select value={value} onValueChange={onValueChange} {...selectProps}>
      <SelectTrigger className="mt-2 w-full">
        <SelectValue placeholder="Select a timezone" />
      </SelectTrigger>
      <SelectContent>
        {CALENDAR_TIMEZONE_OPTIONS.map(({ value: tz, label }) => (
          <SelectItem key={tz} value={tz}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

type TimezoneOption = (typeof CALENDAR_TIMEZONE_OPTIONS)[number]

type TimezoneGroup = {
  value: string
  items: TimezoneOption[]
}

const TIMEZONE_GROUPS: TimezoneGroup[] = [
  {
    value: 'Popular Timezones',
    items: CALENDAR_TIMEZONE_OPTIONS.filter((tz) => tz.popular),
  },
  {
    value: 'All Timezones',
    items: CALENDAR_TIMEZONE_OPTIONS,
  },
]

function timezoneItemToString(item: TimezoneOption): string {
  return `${item.label} ${item.value} ${formatGmtOffset(item.value)}`
}

function CalendarTimeZoneSearchable({
  value,
  onValueChange,
  trigger,
  triggerClassName,
  className,
  open: openProp,
  onOpenChange,
  containerRef,
}: CalendarTimeZoneSearchableProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const open = openProp ?? uncontrolledOpen
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next)
    onOpenChange?.(next)
  }

  const selectedItem = React.useMemo(
    () => CALENDAR_TIMEZONE_OPTIONS.find((tz) => tz.value === value) ?? null,
    [value],
  )

  const triggerClassNameMerged = cn(
    'group flex size-full cursor-pointer flex-col items-start justify-center gap-2 rounded-l-none rounded-r-xl border border-evergreen-200 bg-evergreen-50 px-3 py-2 text-left font-family-body text-sm font-semibold whitespace-nowrap text-accent ring-evergreen-500 ring-offset-transparent transition-all outline-none hover:border-border-hover hover:bg-neutral-100  focus:ring-2 focus:ring-evergreen-500 focus:ring-offset-transparent focus:outline-none focus-visible:ring focus-visible:ring-evergreen-500 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
    triggerClassName,
  )

  return (
    <Combobox
      items={TIMEZONE_GROUPS}
      value={selectedItem}
      onValueChange={(item) => {
        if (item && typeof item === 'object' && 'value' in item && 'label' in item) {
          onValueChange((item as TimezoneOption).value)
        }
      }}
      itemToStringLabel={(item) =>
        item && typeof item === 'object' && 'label' in item
          ? timezoneItemToString(item as TimezoneOption)
          : ''
      }
      itemToStringValue={(item) =>
        item && typeof item === 'object' && 'value' in item
          ? String((item as TimezoneOption).value)
          : ''
      }
      isItemEqualToValue={(a, b) =>
        !!a &&
        !!b &&
        typeof a === 'object' &&
        typeof b === 'object' &&
        'value' in a &&
        'value' in b &&
        (a as TimezoneOption).value === (b as TimezoneOption).value
      }
      open={open}
      onOpenChange={(next) => setOpen(next)}
    >
      {trigger && React.isValidElement(trigger) ? (
        <ComboboxTrigger render={trigger} showChevron={false} />
      ) : (
        <ComboboxTrigger className={triggerClassNameMerged} showChevron={false}>
          <GlobeIcon className="size-4 text-muted-foreground" strokeWidth={1.5} />
          <span className="text-xs font-medium text-foreground">{formatGmtOffset(value)}</span>
          <span className="text-xs text-muted-foreground">{timezoneCityLabel(value)}</span>
        </ComboboxTrigger>
      )}
      <ComboboxContent className={cn('w-80', className)} align="end" container={containerRef}>
        <ComboboxInput placeholder="Search for a timezone" showTrigger={false} />
        <ComboboxEmpty>No timezones found</ComboboxEmpty>
        <ComboboxList>
          {(group: TimezoneGroup, index: number) => (
            <ComboboxGroup key={group.value} items={group.items}>
              <ComboboxLabel>{group.value}</ComboboxLabel>
              <ComboboxCollection>
                {(item: TimezoneOption) => (
                  <ComboboxItem key={item.value} value={item}>
                    <span className="min-w-0 flex-1 truncate font-medium">{item.label}</span>
                    <span className="ml-auto shrink-0 text-xs opacity-80">
                      {formatGmtOffset(item.value)}
                    </span>
                  </ComboboxItem>
                )}
              </ComboboxCollection>
              {index < TIMEZONE_GROUPS.length - 1 ? <ComboboxSeparator /> : null}
            </ComboboxGroup>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export { Calendar, CalendarDayButton, CalendarTimePicker, CalendarTimeZone }
